// Blip in the IDE (docs/BLIP_HELPER_DESIGN.md, "Blip the character"): his
// canvas, his moods and his idle life, drawn with the help's shared script
// (_release/help/js/blip.js), which esbuild bundles here.
//
// States: resting, listening, thinking, reading, writing, watching, talking,
// and the moments pleased and surprised, which go back to resting; sleepy
// when he is out of energy. With reduced motion, each is a still pose. He
// is bigger the harder the user asks him to try (setScale), and grows or
// shrinks to it.

import "../../../../_release/help/js/blip.js";

const MOMENTS = { pleased: 2.2, surprised: 1.6 };
// His size and where he stands, in CSS pixels: his canvas is larger, with
// room above him to hop.
const SIZE = 29, FEET = 6;

export class BlipFigure {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.palette = window.BlipCharacter.palette(document.documentElement);
    this.reduceMotion = matchMedia("(prefers-reduced-motion: reduce)");
    this.state = "resting";
    this.scale = 1;                      // smoothed
    this.scaleTarget = 1;
    this.since = performance.now() / 1000;
    this.look = { x: 0, y: 0 };          // smoothed
    this.lookTarget = { x: 0, y: 0 };
    this.nextBlink = 2;
    this.frame = null;
    this.lastDraw = 0;
    this.visible = true;
    this.pointerNear = false;
    addEventListener("pointermove", (event) => this.pointer(event), { passive: true });
    document.addEventListener("visibilitychange", () => this.schedule());
    this.reduceMotion.addEventListener("change", () => this.schedule());
    this.schedule();
  }

  set(state) {
    if (state === this.state && !MOMENTS[state]) return;
    this.state = state;
    this.since = performance.now() / 1000;
    this.schedule();
  }

  setScale(scale) {
    this.scaleTarget = scale;
    this.schedule();
  }

  show(visible) {
    this.visible = visible;
    this.schedule();
  }

  // He watches the pointer when it comes near.
  pointer(event) {
    const box = this.canvas.getBoundingClientRect();
    const dx = event.clientX - (box.left + box.width / 2), dy = event.clientY - (box.bottom - FEET - SIZE * 0.6);
    const distance = Math.hypot(dx, dy);
    this.pointerNear = distance < 360;
    if (this.pointerNear) {
      this.lookTarget = { x: Math.max(-1, Math.min(1, dx / 160)), y: Math.max(-1, Math.min(1, -dy / 160)) };
      this.schedule();
    }
  }

  schedule() {
    if (this.frame !== null) return;
    if (!this.visible || document.visibilityState === "hidden") return;
    this.frame = requestAnimationFrame((time) => {
      this.frame = null;
      this.draw(time / 1000);
    });
  }

  // How he stands now: the shared script's body.
  pose(t) {
    const still = this.reduceMotion.matches;
    const age = t - this.since;
    const width = this.canvas.width / (devicePixelRatio || 1);
    const height = this.canvas.height / (devicePixelRatio || 1);
    this.scale += (this.scaleTarget - this.scale) * (still ? 1 : 0.15);
    if (Math.abs(this.scaleTarget - this.scale) < 0.002) this.scale = this.scaleTarget;
    const body = { x: width / 2, y: height - FEET, size: SIZE * this.scale, yaw: -16, pitch: 4, roll: 0,
      lift: 0, sq: 0, st: 0, eyes: "normal", mouth: "smile", talk: 0, blink: 0, lookX: 0, lookY: 0, glow: 0 };
    const bob = still ? 3 : 3 + Math.sin(t * 2.1) * 1.6;
    body.lift = bob;
    // Glances, and the pointer.
    let look = { x: 0, y: 0 };
    if (this.pointerNear) look = this.lookTarget;
    else if (!still) look = { x: Math.sin(t * 0.37) * 0.6 * (Math.sin(t * 0.11) > 0.4 ? 1 : 0.2), y: Math.sin(t * 0.23) * 0.2 };
    switch (this.state) {
      case "listening":
        look = { x: -0.5, y: 0.6 };
        body.yaw = -24;
        break;
      case "thinking":
        look = { x: 0.3, y: 1 };
        body.glow = still ? 0.8 : 0.55 + 0.45 * Math.sin(t * 4.5);
        body.yaw = -16 + (still ? 0 : Math.sin(t * 1.4) * 10);
        break;
      case "reading":
        look = { x: -0.9 + (still ? 0 : Math.sin(t * 5) * 0.25), y: 0.4 };
        body.yaw = -30;
        break;
      case "writing":
        look = { x: -0.9, y: 0.2 };
        body.yaw = -34;
        body.roll = still ? 4 : 4 + Math.sin(t * 9) * 3;
        if (!still) {
          body.sq = Math.max(0, Math.sin(t * 9)) * 0.08;
          body.lift = bob * 0.6;
        }
        break;
      case "watching":
        look = { x: -0.4, y: 1 };
        body.eyes = "wide";
        body.yaw = -10;
        body.pitch = -6;
        break;
      case "talking":
        body.mouth = "talk";
        body.talk = still ? 0.5 : Math.abs(Math.sin(t * 11)) * (0.6 + 0.4 * Math.sin(t * 2.3));
        break;
      case "pleased": {
        body.eyes = "happy";
        body.mouth = "grin";
        if (!still && age < 0.9) {
          const p = age / 0.9;
          body.lift = bob + Math.sin(p * Math.PI) * 16;
          body.yaw = -16 + p * 360;
          body.st = Math.sin(p * Math.PI) * 0.1;
        }
        break;
      }
      case "surprised":
        body.eyes = "wide";
        body.mouth = "o";
        if (!still && age < 0.35) body.lift = bob + Math.sin((age / 0.35) * Math.PI) * 7;
        break;
      case "sleepy":
        body.blink = 0.65;
        body.lift = 1;
        body.sq = 0.06;
        body.mouth = !still && Math.sin(t * 0.8) > 0.92 ? "yawn" : "smile";
        look = { x: 0, y: -0.4 };
        break;
      default:
    }
    // Blinks, now and then.
    if (!still && this.state !== "sleepy" && body.eyes === "normal") {
      if (t > this.nextBlink + 0.16) this.nextBlink = t + 2.5 + Math.random() * 3.5;
      if (t > this.nextBlink) body.blink = Math.sin(((t - this.nextBlink) / 0.16) * Math.PI);
    }
    const ease = still ? 1 : 0.12;
    this.look.x += (look.x - this.look.x) * ease;
    this.look.y += (look.y - this.look.y) * ease;
    body.lookX = this.look.x;
    body.lookY = this.look.y;
    return body;
  }

  draw(t) {
    const moment = MOMENTS[this.state];
    if (moment && t - this.since > moment) {
      this.state = "resting";
      this.since = t;
    }
    // Pixels for the screen's density.
    const ratio = devicePixelRatio || 1;
    const box = this.canvas.getBoundingClientRect();
    const width = Math.round(box.width * ratio), height = Math.round(box.height * ratio);
    if (width && height && (this.canvas.width !== width || this.canvas.height !== height)) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    window.BlipCharacter.draw(ctx, this.pose(t), t, { palette: this.palette, reduceMotion: this.reduceMotion.matches });
    this.lastDraw = t;
    // He moves at 30 frames a second while busy, slower at rest, and not
    // at all with reduced motion, except to finish a look.
    if (this.reduceMotion.matches) {
      if (Math.abs(this.look.x - this.lookTarget.x) > 0.01 && this.pointerNear) this.schedule();
      return;
    }
    const busy = (this.state !== "resting" && this.state !== "sleepy") || this.scale !== this.scaleTarget;
    setTimeout(() => this.schedule(), busy || this.pointerNear ? 33 : 66);
  }
}
